import { Chess } from "chess.js";
import type { UciMove } from "./fen";
import { uciToSquares } from "./fen";

/**
 * Use chess.js as a secondary sanity check on engine output.
 *
 * Returns the parsed UCI move iff chess.js (with skipValidation) considers it
 * legal from `fen`. Returns null otherwise. We swallow any chess.js errors —
 * the rogue board may produce unusual positions chess.js refuses to load, in
 * which case the caller will fall back to its own pseudo-legal validation.
 */
export function validateUciMove(fen: string, uci: string): UciMove | null {
  const parsed = uciToSquares(uci);
  if (!parsed) return null;
  try {
    const chess = new Chess(fen, { skipValidation: true });
    const move = chess.move({
      from: squareName(parsed.from.row, parsed.from.col),
      to: squareName(parsed.to.row, parsed.to.col),
      promotion: parsed.promotion as "q" | "r" | "b" | "n" | undefined,
    });
    if (!move) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Pick a random legal move for the side to move using chess.js. Returns null
 * if chess.js cannot evaluate the FEN (e.g., missing king). Useful as a
 * smarter fallback than purely random board picks.
 */
export function randomChessJsMove(fen: string, rand: () => number): UciMove | null {
  try {
    const chess = new Chess(fen, { skipValidation: true });
    const moves = chess.moves({ verbose: true });
    if (moves.length === 0) return null;
    const pick = moves[Math.floor(rand() * moves.length)];
    return uciToSquares(`${pick.from}${pick.to}${pick.promotion ?? ""}`);
  } catch {
    return null;
  }
}

function squareName(row: number, col: number): string {
  const file = String.fromCharCode(97 + col);
  const rank = 8 - row;
  return `${file}${rank}`;
}
